<script setup lang="ts">
/** Timeline verticale du journal d'une demande (le plus récent en haut). */
import { computed, type Component } from 'vue'
import {
  PlusCircleIcon,
  UserPlusIcon,
  ArrowsRightLeftIcon,
  BoltIcon,
  HandThumbDownIcon,
  BellAlertIcon,
  PencilSquareIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import type {
  AccompEventType,
  AccompHistoryEvent,
} from '@/v2/accompagnement/api/accompagnementTypes'
import { formatDateTime } from '@/v2/accompagnement/helpers/accompagnementFormat'

const props = defineProps<{ events: AccompHistoryEvent[] }>()

const META: Record<AccompEventType, { icon: Component; color: string }> = {
  CREATED: { icon: PlusCircleIcon, color: 'text-info' },
  ASSIGNED: { icon: UserPlusIcon, color: 'text-warning' },
  REASSIGNED: { icon: ArrowsRightLeftIcon, color: 'text-warning' },
  ACCEPTED: { icon: BoltIcon, color: 'text-primary' },
  REFUSED: { icon: HandThumbDownIcon, color: 'text-error' },
  REMINDED: { icon: BellAlertIcon, color: 'text-warning' },
  EDITED: { icon: PencilSquareIcon, color: 'text-info' },
  COMMENTED: { icon: ChatBubbleLeftEllipsisIcon, color: 'text-base-content/60' },
  CLOSED: { icon: CheckCircleIcon, color: 'text-success' },
  CANCELLED: { icon: XCircleIcon, color: 'text-base-content/60' },
}

function label(e: AccompHistoryEvent): string {
  switch (e.type) {
    case 'CREATED':
      return 'Demande créée'
    case 'ASSIGNED':
      return `Attribuée à ${e.assignedToName ?? '—'}`
    case 'REASSIGNED':
      return `Réattribuée à ${e.assignedToName ?? '—'}`
    case 'ACCEPTED':
      return 'Prise en charge'
    case 'REFUSED':
      return 'Demande refusée'
    case 'REMINDED':
      return 'Relance'
    case 'EDITED':
      return 'Demande modifiée'
    case 'COMMENTED':
      return 'Commentaire'
    case 'CLOSED':
      return 'Demande terminée'
    case 'CANCELLED':
      return 'Demande annulée'
    default:
      return e.type
  }
}

const ordered = computed(() => [...props.events].reverse())
</script>

<template>
  <ul role="list" class="space-y-0">
    <li v-for="(e, i) in ordered" :key="e.id" class="flex gap-3">
      <!-- Rail + pastille -->
      <div class="flex flex-col items-center">
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-base-200"
          :class="META[e.type].color"
        >
          <component :is="META[e.type].icon" class="h-4 w-4" aria-hidden="true" />
        </span>
        <span
          v-if="i < ordered.length - 1"
          class="w-px flex-1 bg-base-300"
          aria-hidden="true"
        ></span>
      </div>
      <!-- Contenu -->
      <div class="min-w-0 flex-1 pb-5">
        <p class="font-medium text-base-content">{{ label(e) }}</p>
        <p class="text-sm text-base-content/70">
          {{ e.actorName }} · <span class="tabular-nums">{{ formatDateTime(e.at) }}</span>
        </p>
        <p
          v-if="e.note"
          class="mt-1 whitespace-pre-line rounded-md bg-base-200/60 px-3 py-2 text-base-content/80"
        >
          {{ e.note }}
        </p>
      </div>
    </li>
  </ul>
</template>
